import {Alert, Stack, Text, Title} from '@mantine/core';
import React, {useCallback, useMemo} from 'react';

import LinkedSchemaNamingStrategyWarning, {
  type MoleculeStructureKind,
} from 'chemistry/molecules/LinkedSchemaNamingStrategyWarning';
import RegistrationConfigPane from 'registry/create-bioentity/registration-config/RegistrationConfigPane';
import type {DraftRegistrationConfig} from 'registry/create-bioentity/registration-config/RegistrationConfigTypes';
import Explainer from 'spreadsheet-import-wizard/finalize/Explainer';
import {ButtonSize} from 'ui-library/buttons/Button';

import {
  MOLECULE_IMPORT_EXPLAINER_HEADER,
  MOLECULE_IMPORT_EXPLAINER_STEPS,
} from './moleculeImportExplainerSteps';
import type {ModalAction, ModalState} from './moleculeImportReducer';

interface FinalizeImportPaneProps {
  state: ModalState;
  dispatch: React.Dispatch<ModalAction>;
}

/** Bulk import rows may include salted or unsalted molecules, so check both registration paths. */
const BULK_IMPORT_MOLECULE_STRUCTURE_KIND: MoleculeStructureKind = 'mixed';

export default function FinalizeImportPane({state, dispatch}: FinalizeImportPaneProps): JSX.Element {
  const registrationConfig: DraftRegistrationConfig = useMemo(
    () => ({
      registryId: state.registrationConfig?.registryId ?? null,
      namingStrategy: state.registrationConfig?.namingStrategy ?? null,
      folderId: state.registrationConfig?.folderId ?? state.initialFolderId ?? null,
    }),
    [state.registrationConfig, state.initialFolderId]
  );

  const handleRegistrationConfigChange = useCallback(
    (config: DraftRegistrationConfig) => {
      if (config.registryId !== registrationConfig.registryId) {
        dispatch({type: 'SET_REGISTRY_ID', payload: {registryId: config.registryId}});
      }
      if (config.namingStrategy !== registrationConfig.namingStrategy && config.namingStrategy != null) {
        dispatch({type: 'SET_NAMING_STRATEGY', payload: {namingStrategy: config.namingStrategy}});
      }
      if (config.folderId !== registrationConfig.folderId) {
        dispatch({type: 'SET_FOLDER_ID', payload: {folderId: config.folderId}});
      }
    },
    [dispatch, registrationConfig]
  );

  return (
    <Stack spacing="lg">
      <Stack spacing="xxs">
        {/* m={0} neutralizes the global Bootstrap <h5> margin so the Stack's xxs gap governs spacing. */}
        <Title order={5} m={0}>
          Finalize import
        </Title>
        <Text size="md">Review the folder and registry your molecules will be saved to.</Text>
      </Stack>

      {state.errorMessage && (
        <Alert color="red" title="Import failed">
          {state.errorMessage}
        </Alert>
      )}

      <RegistrationConfigPane
        registrationConfig={registrationConfig}
        onRegistrationConfigChange={handleRegistrationConfigChange}
        registryReadOnly={true}
        allowedLabelingStrategies={state.schemaLabelingStrategies ?? undefined}
        folderButtonSize={ButtonSize.SMALL}
        folderRequired={true}
      />

      <LinkedSchemaNamingStrategyWarning
        selectedSchemaId={state.schemaId}
        structureKind={BULK_IMPORT_MOLECULE_STRUCTURE_KIND}
        namingStrategy={state.registrationConfig?.namingStrategy ?? null}
      />

      <Explainer headerTitle={MOLECULE_IMPORT_EXPLAINER_HEADER} steps={MOLECULE_IMPORT_EXPLAINER_STEPS} />
    </Stack>
  );
}
